import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getCurrentUser } from '../../api/common';
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker";
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Divider,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Cake as CakeIcon,
  PhotoCamera as PhotoCameraIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import '../../css/users.css';

function UserEdit() {
  const { id } = useParams();
  const [form, setForm] = useState({
    userid: '',
    name: '',
    role: 'user',
    password: '',
    dept_code: '',
    dept_name: '',
    parent_dept_code: '',
    phone: '',
    email: '',
    position: '',
    birthday: null,
    photo_path: '',
  });

  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`/api/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = res.data;
      setForm({
        userid: user.userid,
        name: user.name || '',
        role: user.role || '',
        password: '',
        dept_code: user.dept_code || '',
        dept_name: user.dept_name || '',
        parent_dept_code: user.parent_dept_code || '',
        phone: user.phone || '',
        email: user.email || '',
        position: user.position || '',
        birthday: user.birthday ? new Date(user.birthday) : null,
        photo_path: user.photo_path || '',
      });
      // 초기 사진 데이터가 있으면 미리보기는 초기화
      setPreviewUrl(null);
    } catch (err) {
      console.error('회원 정보 조회 실패:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        alert('접근 권한이 없습니다');
        navigate('/login');
      } else {
        alert('회원 정보를 불러올 수 없습니다');
      }
    }
  }, [id, token, navigate]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser(token);
      setCurrentUser(user);
    } catch (err) {
      console.error('현재 사용자 조회 실패:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
    fetchCurrentUser();
  }, [fetchUser, fetchCurrentUser]);

  const handleConfirmOrgSelection = (targets) => {
    if (targets.length > 0) {
      const selected = targets[0];
      setForm({
        ...form,
        dept_code: selected.target_id,
        dept_name: selected.target_name,
        parent_dept_code: selected.parent_dept_code || '',
      });
    }
    setShowOrgPopup(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'birthday' && value instanceof Date) {
            formData.append(key, value.toISOString().split('T')[0]);
          } else {
            formData.append(key, value);
          }
        }
      });

      await axios.put(`/api/auth/users/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('사용자 정보가 수정되었습니다');
      navigate(`/users/${id}`);
    } catch (err) {
      console.error('사용자 수정 실패:', err);
      alert(err.response?.data?.message || '수정 중 오류 발생');
    }
  };

  const isNonAdmin       = currentUser?.role !== 'admin';
  const isNotSelfOrAdmin = !(currentUser?.id === parseInt(id) || currentUser?.role === 'admin');

  return (
    <Box sx={{ p: 3, maxWidth: 850, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e4e8', bgcolor: '#fff' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 4,
          pb: 2,
          borderBottom: '2px solid #1976d2'
        }}>
          <EditIcon sx={{ color: '#1976d2', fontSize: '2rem' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              사용자 정보 수정
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              기존 사용자의 정보를 변경하거나 관리합니다.
            </Typography>
          </Box>
        </Box>

        <Stack spacing={3}>
          {/* 사진 및 아이디/이름 행 */}
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={previewUrl || (typeof form.photo_path === 'string' && form.photo_path ? `${form.photo_path}?${new Date().getTime()}` : '')}
                sx={{ width: 140, height: 140, border: '4px solid #f8f9fa', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
              >
                {!previewUrl && !form.photo_path && <PersonIcon sx={{ fontSize: 80, color: '#dee2e6' }} />}
              </Avatar>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<PhotoCameraIcon />}
                disabled={isNotSelfOrAdmin}
                sx={{ borderRadius: 10, px: 2 }}
              >
                사진 변경
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setForm({ ...form, photo_path: file });
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPreviewUrl(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </Button>
            </Box>

            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', color: '#495057' }}>
                    아이디 (수정 불가)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={form.userid}
                    disabled
                    bgcolor="#f8f9fa"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', color: '#495057' }}>
                    이름 <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    disabled={isNonAdmin}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', color: '#495057' }}>
                    권한
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      disabled={isNonAdmin}
                    >
                      <MenuItem value="user">user</MenuItem>
                      <MenuItem value="admin">admin</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', color: '#495057' }}>
                    비밀번호 (변경 시 입력)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="비밀번호 재설정"
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
              부서 선택
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={form.dept_name || ''}
                placeholder="부서를 선택하세요"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountTreeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={() => setShowOrgPopup(true)}
                disabled={isNonAdmin}
                startIcon={<SearchIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                선택
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={isNonAdmin}
                onClick={() => setForm({ ...form, dept_code: '', dept_name: '', parent_dept_code: '' })}
                startIcon={<ClearIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                삭제
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                직책
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="position"
                value={form.position}
                onChange={handleChange}
                disabled={isNonAdmin}
                placeholder="직책 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                연락처
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                disabled={isNotSelfOrAdmin}
                placeholder="연락처 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                이메일 주소
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="email"
                value={form.email}
                onChange={handleChange}
                disabled={isNonAdmin}
                placeholder="이메일 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                생년월일
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CakeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                <CommonDatePicker
                  value={form.birthday}
                  onChange={(date) => setForm({ ...form, birthday: date })}
                  name="birthday"
                  placeholder="생일 선택"
                  disabled={isNotSelfOrAdmin}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={handleUpdate}
              sx={{ px: 8, py: 1, fontWeight: 'bold', borderRadius: 2, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              수정 완료
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              onClick={() => navigate(`/users/${id}`)}
              sx={{ px: 4, py: 1, borderRadius: 2 }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {showOrgPopup && (
        <OrgTreeSelect
          selectionMode="single"
          targetType="dept"
          initialSelected={
            form.dept_code
              ? [{ target_type: 'dept', target_id: form.dept_code, target_name: form.dept_name, disp_target_name: form.dept_name }]
              : []
          }
          onConfirm={handleConfirmOrgSelection}
          onCancel={() => setShowOrgPopup(false)}
        />
      )}
    </Box>
  );
}

export default UserEdit;